import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { CacheLocation } from '@auth0/auth0-spa-js';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { provideNzI18n } from 'ng-zorro-antd/i18n';
import { en_US } from 'ng-zorro-antd/i18n';

import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { ErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { LoaderInterceptor } from './core/interceptors/loader.interceptor';
import { UserMeEffects } from './core/store/user/user.effects';
import {
  USERME_FEATURE_KEY,
  userMeReducer,
} from './core/store/user/user.reducer';
import { TokenInterceptor } from './guards/token-interceptor.service';
import { provideNzIcons } from './icons-provider';
import { AppInitService } from './modules/shared/services/init.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),

    // HTTP client + interceptors
    provideHttpClient(withInterceptorsFromDi()),

    provideAnimations(),

    provideNzI18n(en_US),
    provideNzIcons(),

    // Auth0
    importProvidersFrom(
      AuthModule.forRoot({
        domain: environment.AUTH0_DOMAIN,
        clientId: environment.AUTH0_CLIENTID,
        authorizationParams: {
          redirect_uri: window.location.origin,
          audience: environment.AUTH0_AUDIENCE,
          scope: environment.AUTH0_SCOPE,
        },
        cacheLocation: environment.LOCAL_STORAGE_CACHE as CacheLocation,
        // Skip Auth0's redirect-callback handling anywhere under /integrations so
        // it never consumes the OAuth provider's own code/state (e.g. Google's).
        // Auth0's own login callback lands on the root redirect_uri, never here.
        skipRedirectCallback:
          window.location.pathname.startsWith('/integrations'),
      }),
    ),

    // Root NgRx
    importProvidersFrom(
      StoreModule.forRoot({}),
      EffectsModule.forRoot(),
      StoreDevtoolsModule.instrument({ maxAge: 25 }),
    ),

    // Global user store
    importProvidersFrom(
      StoreModule.forFeature(USERME_FEATURE_KEY, userMeReducer),
      EffectsModule.forFeature([UserMeEffects]),
    ),
    {
      provide: APP_INITIALIZER,
      useFactory: (initService: AppInitService) => () => initService.init(),
      deps: [AppInitService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
  ],
};
